import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useRef, useState } from "react";
import { Badge, Button, Form } from "react-bootstrap";
import { rowLabel, selectableRows } from "../../utils/people.js";
import {
  addQccFinding,
  attachQccFiles,
  createQcc,
  deleteQcc,
  deleteQccFinding,
  errorMessage,
  fileContentUrl,
  isAbortError,
  listQcc,
  listUsers,
  removeQccFile,
  updateQcc,
  updateQccFinding,
} from "../../services/apiClient.js";
import TabHeader from "./TabHeader.jsx";
import { formatDate } from "../../utils/date.js";
import { QCC_SECTIONS, QCC_STAGES, sectionLabel, tradesInSection } from "../../../config/qccTrades.js";

// QCC tab — the LogQCC.aspx port (docs/business-rules/qcc.md). A claim can
// carry many QCC inspections; lifecycle: booked → Inspected → PM Checked.
// Since 2026-09-10 a QCC carries FINDINGS: one line per thing wrong, against
// an item from config/qccTrades.js. No findings = Pass; any finding = Fail.
// The office adds findings here when typing up a QCC done on paper (every one
// in 2026 was); the phone will add its own. Legacy rules kept: "New QCC" only
// once the current one is complete (date + inspected + PM checked), and only
// an untouched QCC can be deleted.

// --- Photos / files ---------------------------------------------------------
function FileTile({ f, onRemove, busy }) {
  return (
    <div className="border rounded p-1 text-center" style={{ width: 92 }}>
      {f.is_image && f.thumb_id ? (
        <a href={fileContentUrl(f.id)} target="_blank" rel="noreferrer">
          <img src={fileContentUrl(f.thumb_id)} alt={f.name}
            style={{ width: 80, height: 60, objectFit: "cover" }} />
        </a>
      ) : (
        <a href={fileContentUrl(f.id, { download: true })} className="small d-block text-truncate" data-hint={f.name}>
          {f.name}
        </a>
      )}
      <span role="button" className={`small text-danger clickable${busy ? " pe-none opacity-50" : ""}`}
        onClick={() => !busy && onRemove(f.id)}>
        remove
      </span>
    </div>
  );
}

// One upload strip: a label, an Add button and the tiles. `files` is already
// the subset that belongs here; `findingId` tags uploads to one finding.
function FileStrip({ label, kind, claimId, qccId, findingId, files, onSaved, onError, compact }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function pickFiles(list) {
    if (!list || list.length === 0) return;
    setBusy(true);
    try {
      onSaved(await attachQccFiles(claimId, qccId, Array.from(list), kind, findingId));
    } catch (err) {
      onError(errorMessage(err, "Unable to upload."));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function remove(fileId) {
    setBusy(true);
    try {
      onSaved(await removeQccFile(claimId, qccId, fileId));
    } catch (err) {
      onError(errorMessage(err, "Unable to remove the file."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={compact ? "" : "mt-2"}>
      <div className="d-flex align-items-center column-gap-2">
        {label ? <span className="small fw-semibold">{label}</span> : null}
        <Button size="sm" variant="outline-secondary" className="py-0" disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-hint={findingId ? "Attach a photo to this finding" : undefined}>
          {compact ? "Photo" : "Add"}
        </Button>
        <input ref={inputRef} type="file" multiple hidden onChange={(e) => pickFiles(e.target.files)} />
      </div>
      {files.length > 0 ? (
        <div className="d-flex flex-wrap gap-2 mt-1">
          {files.map((f) => <FileTile key={f.id} f={f} onRemove={remove} busy={busy} />)}
        </div>
      ) : null}
    </div>
  );
}

// --- Findings ---------------------------------------------------------------
// The picker: the vocabulary grouped Interior / Exterior, trades first, then
// the section-level checks (quality / scope / tidy) phrased as the problem.
function TradeSelect({ value, onChange, autoFocus }) {
  return (
    <ThemedSelect size="sm" value={value} onChange={(e) => onChange(e.target.value)} autoFocus={autoFocus}
      style={{ minWidth: 260 }}>
      <option value="">— What is the finding against? —</option>
      {QCC_SECTIONS.map((s) => (
        <optgroup key={s.key} label={s.label}>
          {tradesInSection(s.key).map((t) => (
            <option key={t.key} value={t.key}>
              {t.kind === "section" ? `${s.label}: ${t.label}` : t.label}
            </option>
          ))}
        </optgroup>
      ))}
    </ThemedSelect>
  );
}

function FindingEditor({ initial, onSave, onCancel, busy, saveLabel = "Add" }) {
  const [tradeKey, setTradeKey] = useState(initial?.trade_key || "");
  const [note, setNote] = useState(initial?.note || "");
  return (
    <div className="border rounded p-2 bg-body-tertiary">
      <div className="d-flex flex-wrap align-items-start column-gap-2 row-gap-2">
        <TradeSelect value={tradeKey} onChange={setTradeKey} autoFocus />
        <Form.Control as="textarea" rows={2} size="sm" value={note} placeholder="What is wrong"
          style={{ flex: "1 1 260px" }} onChange={(e) => setNote(e.target.value)} />
        <div className="d-flex gap-1">
          <Button size="sm" disabled={busy || !tradeKey} onClick={() => onSave({ trade_key: tradeKey, note })}>
            {saveLabel}
          </Button>
          <Button size="sm" variant="outline-secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

function FindingRow({ claimId, qcc, finding, photos, onSaved, onError }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  async function save(data) {
    setBusy(true);
    try {
      onSaved(await updateQccFinding(claimId, qcc.id, finding.id, data));
      setEditing(false);
    } catch (err) {
      onError(errorMessage(err, "Unable to save the finding."));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Remove the finding "${finding.trade_label}"?${photos.length ? " Its photos stay on the QCC." : ""}`)) return;
    setBusy(true);
    try {
      onSaved(await deleteQccFinding(claimId, qcc.id, finding.id), { changed: true });
    } catch (err) {
      onError(errorMessage(err, "Unable to remove the finding."));
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <li className="list-group-item px-2 py-2">
        <FindingEditor initial={finding} onSave={save} onCancel={() => setEditing(false)} busy={busy} saveLabel="Save" />
      </li>
    );
  }
  return (
    <li className="list-group-item px-2 py-2">
      <div className="d-flex flex-wrap align-items-start column-gap-2 row-gap-1">
        <Badge bg={finding.section === "interior" ? "info" : "primary"} className="text-uppercase" style={{ fontSize: "0.65rem" }}>
          {sectionLabel(finding.section)}
        </Badge>
        <span className="fw-semibold">{finding.trade_label}</span>
        {finding.source === "legacy" ? (
          <span className="small text-muted" data-hint="Carried over from the old inspector app's checklist">(inspector app)</span>
        ) : finding.source === "phone" ? (
          <span className="small text-muted">(phone)</span>
        ) : null}
        <span className="ms-auto d-flex column-gap-2 small">
          <span role="button" className="clickable text-body-secondary" onClick={() => setEditing(true)}>edit</span>
          <span role="button" className="clickable text-danger" onClick={remove}>remove</span>
        </span>
      </div>
      {finding.note ? <div className="small mt-1" style={{ whiteSpace: "pre-wrap" }}>{finding.note}</div> : null}
      <div className="mt-1">
        <FileStrip compact kind="results" claimId={claimId} qccId={qcc.id} findingId={finding.id}
          files={photos} onSaved={onSaved} onError={onError} />
      </div>
    </li>
  );
}

function Findings({ claimId, qcc, onSaved, onError }) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const findings = qcc.findings || [];
  const files = qcc.files || [];

  async function add(data) {
    setBusy(true);
    try {
      onSaved(await addQccFinding(claimId, qcc.id, data), { changed: true });
      setAdding(false);
    } catch (err) {
      onError(errorMessage(err, "Unable to add the finding."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3">
      <div className="d-flex align-items-center column-gap-2 mb-1">
        <span className="small fw-semibold">Findings</span>
        <span className="small text-muted">
          {findings.length === 0
            ? "none recorded — no findings means the job passed"
            : `${findings.length} — any finding is a fail`}
        </span>
        {!adding ? (
          <Button size="sm" variant="outline-primary" className="py-0 ms-auto" onClick={() => setAdding(true)}>
            Add a finding
          </Button>
        ) : null}
      </div>
      {findings.length > 0 ? (
        <ul className="list-group list-group-flush border rounded mb-2">
          {findings.map((f) => (
            <FindingRow key={f.id} claimId={claimId} qcc={qcc} finding={f}
              photos={files.filter((x) => x.category === "qcc" && Number(x.finding_id) === Number(f.id))}
              onSaved={onSaved} onError={onError} />
          ))}
        </ul>
      ) : null}
      {adding ? <FindingEditor onSave={add} onCancel={() => setAdding(false)} busy={busy} /> : null}
    </div>
  );
}

// --- The QCC card -----------------------------------------------------------
// STAGED: the card shows what the current stage needs and nothing from a later
// one (Robert 2026-09-10: "a setup QCC inspection, then once it has been
// inspected we have more options").
//   Booked     — date, inspector, notes and photos FOR the inspector, and the
//                Inspected switch that opens the next stage.
//   Inspected  — plus the visit: stage of completion, met client, another
//                visit, findings, inspector and client notes, result photos,
//                the Failed tick and the PM Checked switch.
//   PM Checked — the same, with the Passed / Failed badge; nothing locks
//                (1.0 never locked either — the office fixes mistakes).
// The three switches SAVE ON CHANGE, carrying whatever else is typed in the
// card with them: a stage move is a save, and a half-typed note must not be
// lost when the card refreshes. Adding a finding or a photo refreshes the row
// too, so the form only takes a server value that actually CHANGED and keeps
// the rest of what is being typed.
function qccComplete(q) {
  return q.has_been_inspected && q.pm_checked && !!q.inspection_date;
}

const triState = (v) => (v === true ? "true" : v === false ? "false" : "");

function formFromRow(qcc) {
  return {
    inspection_date: qcc.inspection_date || "",
    inspector_id: qcc.inspector_id || "",
    notes_for_inspector: qcc.notes_for_inspector || "",
    notes: qcc.notes || "",
    client_notes: qcc.client_notes || "",
    stage_of_completion: qcc.stage_of_completion == null ? "" : String(qcc.stage_of_completion),
    met_client_on_site: triState(qcc.met_client_on_site),
    another_visit_required: triState(qcc.another_visit_required),
    has_been_inspected: !!qcc.has_been_inspected,
    pm_checked: !!qcc.pm_checked,
    failed_qcc: !!qcc.failed_qcc,
  };
}

function StageCaption({ children }) {
  return (
    <div className="small text-uppercase text-body-secondary fw-semibold mt-3 mb-1 pt-2 border-top"
      style={{ letterSpacing: ".04em", fontSize: ".7rem" }}>
      {children}
    </div>
  );
}

function QccCard({ claimId, qcc, users, onSaved, onError }) {
  const [f, setF] = useState(() => formFromRow(qcc));
  const [busy, setBusy] = useState(false);
  const serverRef = useRef(formFromRow(qcc));

  useEffect(() => {
    const next = formFromRow(qcc);
    const prev = serverRef.current;
    serverRef.current = next;
    // Take only what the server changed; keep what is being typed.
    setF((cur) => {
      const merged = { ...cur };
      for (const k of Object.keys(next)) if (prev[k] !== next[k]) merged[k] = next[k];
      return merged;
    });
  }, [qcc]);

  const set = (name, value) => setF((cur) => ({ ...cur, [name]: value }));

  async function persist(form) {
    setBusy(true);
    try {
      onSaved(await updateQcc(claimId, qcc.id, form), { changed: true });
    } catch (err) {
      onError(errorMessage(err, "Unable to save the QCC inspection."));
      throw err;
    } finally {
      setBusy(false);
    }
  }

  const save = () => persist(f).catch(() => {});

  // A switch is a stage move: it saves at once, with the rest of the card.
  async function toggle(name, value) {
    const next = { ...f, [name]: value };
    setF(next);
    try {
      await persist(next);
    } catch {
      setF((cur) => ({ ...cur, [name]: !value }));
    }
  }

  async function remove() {
    if (!window.confirm("Delete this QCC inspection?")) return;
    setBusy(true);
    try {
      onSaved(await deleteQcc(claimId, qcc.id), { changed: true });
    } catch (err) {
      onError(errorMessage(err, "Unable to delete the QCC inspection."));
    } finally {
      setBusy(false);
    }
  }

  const findings = qcc.findings || [];
  const files = qcc.files || [];
  const inspected = !!f.has_been_inspected;
  // Booked = date + inspector, as typed; nothing is inspected before that.
  const booked = !!f.inspection_date && !!f.inspector_id;
  const untouched = !qcc.has_been_inspected && !qcc.pm_checked && findings.length === 0 && files.length === 0;
  // The tick and the findings can disagree only by the office's own hand;
  // say so rather than hide it.
  const disagree = qcc.has_been_inspected && findings.length > 0 && !qcc.failed_qcc;

  return (
    <div className="border rounded p-3 mb-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center column-gap-3 row-gap-1 mb-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="fw-semibold">
            {qcc.inspection_date ? `Inspection on ${formatDate(qcc.inspection_date)}` : "Inspection date unknown"}
          </span>
          {qccComplete(qcc) ? (
            qcc.failed_qcc ? <Badge bg="danger">Failed</Badge> : <Badge bg="success">Passed</Badge>
          ) : qcc.has_been_inspected ? (
            <Badge bg="warning" text="dark">Awaiting PM check</Badge>
          ) : qcc.inspection_date && qcc.inspector_id ? (
            <Badge bg="secondary">Booked</Badge>
          ) : (
            // A bare row from New QCC: booked means a date AND an inspector,
            // and the rail's "QCC Inspections Booked" counts only those.
            <Badge bg="light" text="dark" className="border">New</Badge>
          )}
          {disagree ? (
            <span className="small text-warning-emphasis" data-hint="Findings are recorded but the Failed tick is off — a QCC with findings is normally a fail">
              has findings, not marked failed
            </span>
          ) : null}
          {qcc.finished_by_inspector_at ? (
            <span className="small text-muted">
              finished on the phone {formatDate(qcc.finished_by_inspector_at)}
            </span>
          ) : null}
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" onClick={save} disabled={busy}>Save</Button>
          {untouched ? (
            <Button size="sm" variant="outline-danger" onClick={remove} disabled={busy}
              data-hint="Only an untouched QCC can be deleted (legacy rule)">
              Delete
            </Button>
          ) : null}
        </div>
      </div>

      {/* Stage 1 — the booking */}
      <div className="d-flex flex-wrap column-gap-3 row-gap-2 mb-2">
        <div>
          <Form.Label className="mb-0 small fw-semibold">Inspection Date</Form.Label>
          <Form.Control size="sm" type="date" value={f.inspection_date || ""} style={{ width: 160 }}
            onChange={(e) => set("inspection_date", e.target.value)} />
        </div>
        <div>
          <Form.Label className="mb-0 small fw-semibold">Inspector</Form.Label>
          <ThemedSelect size="sm" value={f.inspector_id || ""} style={{ width: 200 }}
            onChange={(e) => set("inspector_id", e.target.value)}>
            <option value="">— None —</option>
            {/* The Inspector group (the same list the Inspection tab offers). Retired
                accounts aren't offered, but the inspector this QCC already names
                stays listed — see utils/people.js. */}
            {selectableRows(users, f.inspector_id).map((u) => (
              <option key={u.id} value={u.id}>{rowLabel(u)}</option>
            ))}
          </ThemedSelect>
        </div>
        <div className="d-flex align-items-end column-gap-3 pb-1">
          <Form.Check type="switch" id={`qcc-insp-${qcc.id}`} label="Inspected" disabled={busy || (!inspected && !booked)}
            checked={inspected} onChange={(e) => toggle("has_been_inspected", e.target.checked)}
            data-hint={inspected ? undefined : booked
              ? "Tick once the visit has happened — the result fields open up"
              : "Book it first: a date and an inspector"} />
        </div>
      </div>
      <div className="row g-2">
        <div className="col-md-6">
          <Form.Label className="mb-0 small fw-semibold">Notes For Inspector</Form.Label>
          <Form.Control as="textarea" rows={3} size="sm" value={f.notes_for_inspector || ""}
            onChange={(e) => set("notes_for_inspector", e.target.value)} />
        </div>
        <div className="col-md-6 d-flex align-items-end">
          <FileStrip label="Photos For Inspector" kind="for_inspector" claimId={claimId} qccId={qcc.id}
            files={files.filter((x) => x.category === "qcc_for_inspector")} onSaved={onSaved} onError={onError} />
        </div>
      </div>

      {/* Stage 2 — the visit and its result; Stage 3 adds only the PM's tick */}
      {inspected ? (
        <>
          <StageCaption>The visit</StageCaption>
          <div className="d-flex flex-wrap column-gap-3 row-gap-2 mb-2">
            <div>
              <Form.Label className="mb-0 small fw-semibold">Stage of completion</Form.Label>
              <ThemedSelect size="sm" value={f.stage_of_completion || ""} style={{ width: 130 }}
                onChange={(e) => set("stage_of_completion", e.target.value)}>
                <option value="">—</option>
                {QCC_STAGES.map((s) => <option key={s} value={String(s)}>{s}%</option>)}
              </ThemedSelect>
            </div>
            <div>
              <Form.Label className="mb-0 small fw-semibold">Met client on site</Form.Label>
              <ThemedSelect size="sm" value={f.met_client_on_site || ""} style={{ width: 130 }}
                onChange={(e) => set("met_client_on_site", e.target.value)}>
                <option value="">—</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </ThemedSelect>
            </div>
            <div>
              <Form.Label className="mb-0 small fw-semibold">Another visit required</Form.Label>
              <ThemedSelect size="sm" value={f.another_visit_required || ""} style={{ width: 130 }}
                onChange={(e) => set("another_visit_required", e.target.value)}>
                <option value="">—</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </ThemedSelect>
            </div>
          </div>

          <Findings claimId={claimId} qcc={qcc} onSaved={onSaved} onError={onError} />

          <div className="row g-2 mt-1">
            <div className="col-md-6">
              <Form.Label className="mb-0 small fw-semibold">Inspector Notes</Form.Label>
              <Form.Control as="textarea" rows={3} size="sm" value={f.notes || ""}
                onChange={(e) => set("notes", e.target.value)} />
            </div>
            <div className="col-md-6">
              <Form.Label className="mb-0 small fw-semibold">Client Notes</Form.Label>
              <Form.Control as="textarea" rows={3} size="sm" value={f.client_notes || ""}
                onChange={(e) => set("client_notes", e.target.value)} />
            </div>
          </div>

          {/* Result photos not tagged to a finding; tagged ones sit under their finding. */}
          <FileStrip label="QCC Files" kind="results" claimId={claimId} qccId={qcc.id}
            files={files.filter((x) => x.category === "qcc" && !x.finding_id)} onSaved={onSaved} onError={onError} />

          <StageCaption>Result</StageCaption>
          <div className="d-flex align-items-center column-gap-4 flex-wrap">
            <Form.Check type="switch" id={`qcc-fail-${qcc.id}`} label="Failed QCC" disabled={busy}
              checked={!!f.failed_qcc} onChange={(e) => toggle("failed_qcc", e.target.checked)}
              data-hint="Set for you when a finding is added; any finding is a fail" />
            <Form.Check type="switch" id={`qcc-pm-${qcc.id}`} label="PM Checked" disabled={busy}
              checked={!!f.pm_checked} onChange={(e) => toggle("pm_checked", e.target.checked)}
              data-hint="The PM's sign-off on the result — the last step" />
          </div>
        </>
      ) : null}
    </div>
  );
}

function ClaimQccTab({ claimId, onChanged }) {
  const [inspections, setInspections] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    setError("");
    listQcc(claimId)
      .then((rows) => !ac.signal.aborted && setInspections(rows))
      .catch((err) => {
        if (isAbortError(err) || ac.signal.aborted) return;
        setError(errorMessage(err, "Unable to load QCC inspections."));
      });
    listUsers({ inspectors: true, signal: ac.signal })
      .then((rows) => !ac.signal.aborted && setUsers(rows))
      .catch(() => {});
    return () => ac.abort();
  }, [claimId]);

  // Every mutation hands back the fresh list; `changed` means a flag or a
  // finding moved, which the rail's QCC badges read.
  function saved(rows, { changed = false } = {}) {
    setInspections(rows);
    if (changed) onChanged?.();
  }

  const hasIncomplete = (inspections || []).some((q) => !qccComplete(q));

  async function addNew() {
    setBusy(true);
    setError("");
    try {
      saved(await createQcc(claimId), { changed: true });
    } catch (err) {
      setError(errorMessage(err, "Unable to create a QCC inspection."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <TabHeader
        title="QCC"
        help="qcc"
        actions={
          <Button size="sm" onClick={addNew} disabled={busy || hasIncomplete}
            data-hint={hasIncomplete ? "Finish the current QCC (date + inspected + PM checked) before booking another (legacy rule)" : undefined}>
            New QCC
          </Button>
        }
      />
      {error ? <div className="alert alert-danger">{error}</div> : null}
      {!inspections && !error ? (
        <p className="text-muted">Loading…</p>
      ) : !inspections ? null : inspections.length === 0 ? (
        <p className="text-muted">No QCC inspections on this job — book one with New QCC.</p>
      ) : (
        inspections.map((q) => (
          <QccCard key={q.id} claimId={claimId} qcc={q} users={users}
            onSaved={saved} onError={setError} />
        ))
      )}
    </>
  );
}

export default ClaimQccTab;
